import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  followTask,
  readTaskFollow,
  unfollowTask,
} from '@/lib/api/generated/api';
import type { TaskFollowState } from '@/lib/api/generated/model';

import { notificationKeys } from '../queryKeys';

/**
 * Whether the signed-in person follows a task, and the toggle (spec 131 US3).
 * The toggle flips at once and flips back with a toast if the service refuses.
 */
export function useFollow(workspaceId: string, taskId: string) {
  const queryClient = useQueryClient();
  const key = notificationKeys.follow(workspaceId, taskId);

  const state = useQuery({
    queryKey: key,
    queryFn: async (): Promise<TaskFollowState> => {
      const response = await readTaskFollow(workspaceId, taskId);
      if (response.status !== 200) throw new Error('follow state unavailable');
      return response.data;
    },
    retry: false,
  });

  const toggle = useMutation({
    mutationFn: async (follow: boolean): Promise<TaskFollowState> => {
      const response = follow
        ? await followTask(workspaceId, taskId)
        : await unfollowTask(workspaceId, taskId);
      if (response.status !== 200) throw new Error('refused');
      return response.data;
    },
    onMutate: async (follow) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<TaskFollowState>(key);
      queryClient.setQueryData<TaskFollowState>(key, {
        task_id: taskId,
        following: follow,
        reason: follow ? 'manual' : (previous?.reason ?? null),
      });
      return { previous };
    },
    onSuccess: (next) => {
      queryClient.setQueryData(key, next);
    },
    onError: (_error, _follow, context) => {
      queryClient.setQueryData(key, context?.previous);
      toast.error('Following could not be changed.');
    },
  });

  return {
    state: state.data,
    isPending: state.isPending,
    setFollowing: toggle.mutate,
    isSaving: toggle.isPending,
  };
}
